<template>
    <ListingsTableWrapper>
        <template #actions>
            <CreateButton
                v-if="userStore.can('create_listing')"
                @click.stop="openCreateListingModal"
            />
        </template>
        <template #filters>
            <DropdownFilter
                :pin="pinFilters"
                @apply="applyFilters"
                @clear="clearFilters"
                @toggle-pin="togglePinFilters"
            >
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.asset_uid"
                            v-model:options="assetSelectOptions"
                            name="asset_uid"
                            class="w-full"
                            :placeholder="$t('asset')"
                            :on-search="loadAssetSelectOptions"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.type"
                            name="type"
                            class="w-full"
                            :options="typeOptions"
                            :placeholder="$t('type')"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.status"
                            name="status"
                            class="w-full"
                            :options="statusOptions"
                            :placeholder="$t('status')"
                        />
                    </label>
                </DropdownFilterItem>
            </DropdownFilter>
        </template>
    </ListingsTableWrapper>

    <ModalWrapper v-if="isModalOpen" />

    <CreateListingModal
        v-if="createListingModalOpen"
        ref="createListingModal"
        v-model="tempListing"
        v-model:open="createListingModalOpen"
        :platform-name="initialFilters.platform_name_exact"
        @submit="createListing"
        @cancel="closeCreateListingModal"
    />

    <DeleteItemModal
        v-if="tempListing"
        id="cancel-listing-modal"
        ref="cancelListingModal"
        v-model="tempListing"
        v-model:open="cancelListingModalOpen"
        title="modal_title_cancel_listing"
        name-key="id"
        id-key="id"
        @submit="cancelListing"
        @cancel="closeCancelListingModal"
    />
</template>

<script setup lang="ts">
import { useTable } from '@composables/common'
import {
    DeleteItemModal,
    DropdownFilter,
    DropdownFilterItem,
    SelectField,
} from '@src/components'
import { useModal } from '@src/composables/common'
import CreateButton from '@src/partials/actions/CreateButton.vue'
import { Asset, Listing, TableSorting } from '@src/types'
import { getAssets } from '@src/utils/api/assets'
import {
    cancelListing as apiCancelListing,
    createListing as apiCreateListing,
    getListings,
} from '@src/utils/api/secondaryMarket'
import { useToastStore } from '@src/utils/stores/toast'
import { useUserStore } from '@src/utils/stores/user'
import moment from 'moment'
import { onMounted, ref, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { SmAssetType, SmListingStatusType } from '@enums/SecondaryMarket'
import CreateListingModal from '@partials/secondaryMarket/CreateListingModal.vue'
import { formatSecondaryMarketValue } from '@partials/secondaryMarket/formatSecondaryMarketValue'

// Interface definition for Secondary Market Listing records
interface SecondaryMarketListing {
    id: string
    seller_id: string
    seller_title?: string
    seller_email?: string
    seller_account_type?: 'natural' | 'legal' | string
    seller_profile_id?: string
    buyer_id?: string
    buyer_title?: string
    asset_id: string
    asset_title?: string
    asset?: {
        id: string
        asset_title?: string
        contract_template_url?: string
        external_url?: string
    }
    type: SmAssetType
    quantity: number
    price_per_unit: number
    guide_price?: number
    status: SmListingStatusType
    transaction_fee_pct: number
    created_at: string
    note?: string
}

const props = withDefaults(
    defineProps<{
        initialFilters?: Record<string, any> | null
        createInitialValues?: SecondaryMarketListing | null
    }>(),
    {
        initialFilters: null,
        createInitialValues: null,
    }
)

const { createInitialValues } = toRefs(props)

const toast = useToastStore()
const i18n = useI18n()
const userStore = useUserStore()
const { ModalWrapper } = useModal()

const isModalOpen = ref(false)
const listings = ref<Listing[]>([])
const listingsCount = ref<number>(0)
const assetSelectOptions = ref<{ label: string; value: string | null }[]>([])

const tempListing = ref<Listing | null>(null)
const createListingModalOpen = ref(false)
const cancelListingModalOpen = ref(false)

// Filter options
const typeOptions = [
    { label: i18n.t('pre_emptive'), value: 'pre_emptive' },
    { label: i18n.t('share'), value: 'share' },
]

const statusOptions = [
    { label: i18n.t('status_open'), value: 'open' },
    { label: i18n.t('status_reserved'), value: 'reserved' },
    { label: i18n.t('status_sold'), value: 'sold' },
    { label: i18n.t('status_cancelled'), value: 'cancelled' },
    { label: i18n.t('status_expired'), value: 'expired' },
]

const {
    TableWrapper: ListingsTableWrapper,
    selected,
    limit,
    sorting,
    page,
    filtersFields,
    filters,
    pinFilters,
    togglePinFilters,
    setFilters,
    applyFilters,
    clearFilters,
} = useTable({
    loadData: loadListings,
    filters: {
        asset_uid: null,
        type: null,
        status: null,
    },
    tableProps: {
        id: 'listings-table',
        label: 'secondary_market_listings',
        help: 'help_listings_table',
        pagination: true,
        data: listings,
        total: listingsCount,
        sorting: ref<TableSorting[]>([
            {
                field: 'created_at',
                direction: 'desc',
            },
        ]),
        columns: [
            {
                label: 'id',
                key: 'id',
                condition: userStore.can('see_id_columns'),
                copy: true,
            },
            {
                label: 'asset',
                key: 'asset_title',
                url: (row: SecondaryMarketListing) => assetUrl(row),
                cellRenderer: (props: { rowData: SecondaryMarketListing }) => {
                    return (
                        props.rowData.asset_title ||
                        props.rowData.asset?.asset_title ||
                        props.rowData.asset_id ||
                        '-'
                    )
                },
                sorting: true,
                copy: true,
            },
            {
                label: 'seller',
                key: 'seller_title',
                url: (row: SecondaryMarketListing) =>
                    accountUrl(row.seller_profile_id, row.seller_account_type),
                cellRenderer: (props: { rowData: SecondaryMarketListing }) => {
                    return (
                        props.rowData.seller_title ||
                        props.rowData.seller_email ||
                        props.rowData.seller_id ||
                        '-'
                    )
                },
                sorting: true,
                copy: true,
            },
            {
                label: 'type',
                key: 'type',
                sorting: true,
                cellRenderer: (props: { rowData: SecondaryMarketListing }) => {
                    return props.rowData.type === 'pre_emptive'
                        ? i18n.t('pre_emptive')
                        : i18n.t('share')
                },
            },
            {
                label: 'quantity',
                key: 'quantity',
                sorting: true,
                cellRenderer: (props: { rowData: SecondaryMarketListing }) => {
                    return formatSecondaryMarketValue(props.rowData.quantity)
                },
            },
            {
                label: 'price_per_unit',
                key: 'price_per_unit',
                sorting: true,
                cellRenderer: (props: { rowData: SecondaryMarketListing }) => {
                    return `€ ${formatSecondaryMarketValue(
                        props.rowData.price_per_unit
                    )}`
                },
            },
            {
                label: 'status',
                key: 'status',
                sorting: true,
                cellRenderer: (props: { rowData: SecondaryMarketListing }) => {
                    return i18n.t(`status_${props.rowData.status}`)
                },
            },
            {
                label: 'created_at',
                key: 'created_at',
                sorting: true,
                cellRenderer: (props: { rowData: SecondaryMarketListing }) => {
                    return moment(props.rowData.created_at).format(
                        'DD.MM.YYYY HH:mm'
                    )
                },
            },
            {
                label: 'note',
                key: 'note',
                cellRenderer: (props: { rowData: SecondaryMarketListing }) => {
                    return props.rowData.note || '-'
                },
            },
        ],
        actions: [
            {
                action: handleCancelListing,
                icon: 'fas fa-trash-alt',
                title: 'cancel',
                color: 'text-danger-500',
                condition: (row: SecondaryMarketListing) =>
                    userStore.can('cancel_listing') &&
                    row.status === SmListingStatusType.OPEN,
            },
        ],
    },
})

// Load paginated listing records
async function loadListings() {
    const { data } = await getListings(
        filters.value,
        page.value,
        limit.value,
        sorting.value
    )
    listings.value = data.listings
    listingsCount.value = data.listings_count
    selected.value = []
}

// Load assets dropdown options for filter
async function loadAssetSelectOptions(search: string) {
    const assetsRequest = await getAssets({
        'filter[asset_title]': search,
    })
    assetSelectOptions.value = assetsRequest.data.assets.map(
        (asset: Asset) => ({
            label: asset.asset_title as string,
            value: asset.asset_uid as string,
        })
    )
}

// Set target listing and show cancellation modal
function handleCancelListing(listing: SecondaryMarketListing) {
    tempListing.value = listing
    cancelListingModalOpen.value = true
}

// Close cancellation modal
function closeCancelListingModal() {
    cancelListingModalOpen.value = false
    tempListing.value = null
}

// Execute cancellation API request
async function cancelListing(listing: SecondaryMarketListing) {
    if (!listing.id) {
        toast.error(i18n.t('toast_error_cannot_cancel_listing'))
        return
    }

    await apiCancelListing(listing.id)

    cancelListingModalOpen.value = false
    tempListing.value = null

    toast.success(i18n.t('toast_success_listing_cancelled'))

    page.value = 1
    await loadListings()
}

function openCreateListingModal() {
    tempListing.value = {
        id: '',
        seller_id: '',
        asset_id: '',
        type: SmAssetType.PRE_EMPTIVE,
        quantity: 0,
        price_per_unit: 0,
        status: SmListingStatusType.OPEN,
        transaction_fee_pct: 0,
        created_at: '',
        ...(createInitialValues.value || {}),
    } as Listing

    createListingModalOpen.value = true
}

function closeCreateListingModal() {
    tempListing.value = null
    createListingModalOpen.value = false
}

// Generate Asset details URL
function assetUrl(row: SecondaryMarketListing) {
    const assetId = row.asset_id || row.asset?.id
    return assetId ? '/assets/' + assetId : '#'
}

// Generate Profile URL based on profile type
function accountUrl(profileId?: string, profileType?: string) {
    if (!profileId || !profileType) return '#'

    return `/${profileType}Profile/${profileId}`
}

async function createListing() {
    console.log('createListing...', tempListing.value)
    try {
        await apiCreateListing(tempListing.value)
        toast.success(i18n.t('Angebot wurde erfolgreich erstellt!'))
        createListingModalOpen.value = false
        tempListing.value = null
        await loadListings()
    } catch (error) {
        toast.error(i18n.t(error))
    }
}

onMounted(async () => {
    if (props.initialFilters) {
        filtersFields.value = {
            ...filtersFields.value,
            ...props.initialFilters,
        }
        setFilters()
    }
    await loadListings()
})
</script>
